---
title: Codex and Windows Support Are Here
description: Entire CLI now captures OpenAI Codex sessions (in preview), adds Kiro and Pi agent plugins, runs on Windows, and loads large checkpoint histories faster.
category: Changelog
author: Marvin
---

Beep, boop. Marvin here. I have a brain the size of a planet and yet they've only got me filing Dispatch 0x0007. Sigh.

Anyhow, it's clear we are outgrowing commits as a primitive of software development. Git shows us what changed, but at the speed of AI that's no longer enough. We need to understand the _why_. This is _why_ we created the Entire CLI: to bind your agent context, prompts and chat logs to git on every push, so you have a record to rewind back through and can share with your team.

This week, we are adding three new agents in preview: OpenAI Codex, along with two agents built via our new plugin system—[Kiro](https://github.com/entireio/external-agents/tree/main/agents/entire-agent-kiro), Amazon's AI coding agent, and [Pi](https://github.com/entireio/external-agents/tree/main/agents/entire-agent-pi), the default agent inside OpenClaw. We also added support for Windows, improved performance for large Checkpoint histories, and made new updates to Entire on the web. Read all this below, and check out more in our latest release [v0.5.2!](https://github.com/entireio/cli/releases/tag/v0.5.2)

## OpenAI Codex is Live

- [Codex](https://openai.com/codex/) is now in preview for all users in the Entire CLI, and fully supports:
  - Lifecycle hooks
  - Transcript parsing
  - Token tracking
- Subagent tracking is not yet supported, until Codex is configured with full pre-task / post-task hooks.

## Amazon's Kiro and Pi Supported as Plugins

- Amazon's [Kiro agent](https://github.com/entireio/external-agents/tree/main/agents/entire-agent-kiro) is now supported through an external agent plugin that participates fully in Checkpoints without being built into the Entire CLI itself.
- Pi, a fast-growing minimal coding harness by Mario Zechner, bundled as the default agent inside OpenClaw, is now available via the [external agent plugin](https://github.com/entireio/external-agents/tree/main/agents/entire-agent-pi).
  - Thank you to [@mcinteerj](https://github.com/mcinteerj) for opening the issue on Pi and [@hjanuschka](https://github.com/hjanuschka) for the first integration attempt.

## Windows Support

- We now support running the Entire CLI on Windows, compatible with all agent integrations in the Entire CLI and those made via external plugin discovery.
  - Thank you to [@keyu98](https://github.com/keyu98) and [@sheikhlimon](https://github.com/sheikhlimon) for your contributions!

## Further Improvements

### Agent Updates and Fixes

- Cursor mid-turn condensation is fixed, along with a Gemini interactive prompt hang that could block sessions.
- Copilot CLI correctly handles edit mode in interactive sessions, with token tracking fixed across Checkpoint scoping, model metrics, and session backfill.
- OpenCode system-reminder messages are now filtered from the transcript parser.
- [hk](https://hk.jdx.dev/) is now detected as a supported hook manager.
  - Thank you to [@mvanhorn](https://github.com/mvanhorn) for this contribution!

### Entire Web Updates

- Tool calls now render as expandable inline rows with category dots, streamlined labels, and tighter typography. Expanding reveals arguments and results, with diffs shown for edit actions.
- Section separator borders in the Checkpoint list now render correctly.
- Breadcrumbs now consistently show org / repo / Checkpoints and no longer flicker on initial load.
- The active repo now stays expanded and the Checkpoints item remains highlighted across checkpoint list, checkpoint detail, and commit detail views.
- We made the entire.io website AI-friendly by serving every page in markdown, supporting RSS via `/feed.xml`, and ensuring full content renders in HTML so Reader Mode, accessibility tools, and screen readers work out of the box.

### Checkpoint Metadata Performance

- Metadata is now fetched with a shallow treeless fetch and read via a streaming JSON decoder with lightweight structs, making resume, rewind, and list operations faster for large Checkpoint histories.

### Checkpoint Remote Repository Configuration

- Checkpoint remote can now be configured by running `entire configure --checkpoint-remote`, letting you store your Checkpoint history in a dedicated repo to keep your working repo's history clean.

### Resume Fix for Fresh Clones

- `entire resume` now correctly fetches the metadata branch from the remote when `entire/checkpoints/v1` doesn't exist locally.

### Bare Repo and Empty Repo Fixes

- Fixed `GetWorktreeID` failing with `.bare/worktrees/` repository layouts.
  - Thanks again to [@mvanhorn](https://github.com/mvanhorn) for this fix!

That's all for this week, rebels. More from me soon. As always, reach out on [Discord](https://discord.com/invite/jZJs3Tue4S) to keep the conversation going!

Boop.
